/**
 * Secure Proxy Whitelist — History panel (UI-08).
 *
 * Collapsible "Recent changes" section rendered inside the
 * `SecureProxyWhitelistDialog` (above the footer). Starts collapsed; fetches
 * up to the last 10 audit rows via `useSecureProxyHistory` when expanded.
 *
 * Schema note (matches backend openapi.yml `SecureProxyUseCaseHistoryEntry`):
 *   Each entry is a FULL configuration snapshot — the backend does NOT return
 *   pre-computed `before` / `after` fields. Diffs are therefore computed
 *   client-side by comparing each row's `configuration` against the
 *   chronologically-previous row's `configuration`. The oldest row has no
 *   predecessor, so its "added" list is simply everything in its own snapshot.
 *
 * Decisions honored:
 *   - D-04: no nested dialog (we render inside the editor Dialog).
 *   - D-05: restore only pre-populates drafts — staff still clicks Save.
 *   - D-07: on 404/501 we surface a muted deferral line; we do NOT invent a
 *     direct-data-store or admin-portal-api fallback.
 */
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'

import { SecureProxyHistoryRow } from '@/api/erp-integration-client'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import { useSecureProxyHistory } from '@/services/erp-integration.service'

export interface SecureProxyWhitelistHistoryProps {
  /** Target organization id; threaded through as `x-ivy-org-id` on the call. */
  orgId: string
  integrationId: string | undefined
  useCaseId: string | undefined
  onRestore: (snapshot: {
    allowed_domains: string[]
    allowed_ips: string[]
  }) => void
}

interface RowDiff {
  added: string[]
  removed: string[]
  addedIps: string[]
  removedIps: string[]
}

// --- pure helpers ----------------------------------------------------------

const diffStringLists = (
  previous: readonly string[] | undefined,
  current: readonly string[] | undefined
): { added: string[]; removed: string[] } => {
  const prev = previous ?? []
  const curr = current ?? []
  const prevSet = new Set(prev)
  const currSet = new Set(curr)

  return {
    added: curr.filter((value) => !prevSet.has(value)),
    removed: prev.filter((value) => !currSet.has(value))
  }
}

/**
 * Compute the diff between this history row and the chronologically-previous
 * one. If `previous` is undefined (i.e. the oldest known entry) everything in
 * the current snapshot counts as "added".
 */
export const summarizeRowChange = (
  row: SecureProxyHistoryRow,
  previous: SecureProxyHistoryRow | undefined
): RowDiff => {
  const domains = diffStringLists(
    previous?.configuration?.allowed_domains,
    row.configuration?.allowed_domains
  )
  const ips = diffStringLists(
    previous?.configuration?.allowed_ips,
    row.configuration?.allowed_ips
  )

  return {
    added: domains.added,
    removed: domains.removed,
    addedIps: ips.added,
    removedIps: ips.removed
  }
}

/**
 * Sort raw entries oldest-first so each entry can be diffed against its
 * predecessor, then annotate each with its diff. The final array is returned
 * newest-first for UI display (latest change on top).
 */
export const buildHistoryView = (
  rows: readonly SecureProxyHistoryRow[]
): Array<{ row: SecureProxyHistoryRow; diff: RowDiff }> => {
  const oldestFirst = [...rows].sort((a, b) => {
    const aTime = Date.parse(a.history_created_at)
    const bTime = Date.parse(b.history_created_at)

    return aTime - bTime
  })

  const annotated = oldestFirst.map((row, index) => ({
    row,
    diff: summarizeRowChange(row, oldestFirst[index - 1])
  }))

  return annotated.reverse()
}

// --- component -------------------------------------------------------------

export function SecureProxyWhitelistHistory({
  orgId,
  integrationId,
  useCaseId,
  onRestore
}: SecureProxyWhitelistHistoryProps): JSX.Element {
  const [open, setOpen] = useState(false)

  // Lazy-fetch: only enable the query once the panel has been expanded at
  // least once. This keeps the network quiet for staff who never open it.
  const historyQuery = useSecureProxyHistory(
    orgId,
    integrationId,
    useCaseId,
    {
      limit: 10,
      enabled: open
    }
  )

  const renderBody = (): JSX.Element => {
    if (historyQuery.isEndpointUnavailable) {
      return (
        <p className="text-muted-foreground text-sm">
          History not available in this environment.
        </p>
      )
    }

    if (historyQuery.isLoading) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )
    }

    if (historyQuery.isError) {
      const message =
        historyQuery.error instanceof Error
          ? historyQuery.error.message
          : 'Failed to load history.'

      return <p className="text-muted-foreground text-sm">{message}</p>
    }

    const rows = historyQuery.data

    if (!rows || rows.length === 0) {
      return <p className="text-muted-foreground text-sm">No changes yet.</p>
    }

    const view = buildHistoryView(rows)

    return (
      <ul className="space-y-3">
        {view.map(({ row, diff }) => {
          const timestampLabel = (() => {
            const parsed = Date.parse(row.history_created_at)

            return Number.isNaN(parsed)
              ? row.history_created_at
              : new Date(parsed).toLocaleString()
          })()

          const hasAnyChange =
            diff.added.length +
              diff.removed.length +
              diff.addedIps.length +
              diff.removedIps.length >
            0

          return (
            <li
              className="flex items-start justify-between gap-3 rounded-md border border-border p-3"
              key={row.id}
            >
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm font-medium text-primary">
                  {timestampLabel}
                </p>
                {row.changed_by && (
                  <p className="text-xs text-muted-foreground">
                    {row.changed_by}
                  </p>
                )}
                {hasAnyChange ? (
                  <ul className="text-xs space-y-0.5 pt-1">
                    {diff.added.length > 0 && (
                      <li className="text-green-600 dark:text-green-400">
                        + {diff.added.join(', ')}
                      </li>
                    )}
                    {diff.removed.length > 0 && (
                      <li className="text-destructive">
                        − {diff.removed.join(', ')}
                      </li>
                    )}
                    {diff.addedIps.length > 0 && (
                      <li className="text-green-600 dark:text-green-400">
                        + {diff.addedIps.join(', ')}
                      </li>
                    )}
                    {diff.removedIps.length > 0 && (
                      <li className="text-destructive">
                        − {diff.removedIps.join(', ')}
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground pt-1">
                    No whitelist changes in this entry.
                  </p>
                )}
              </div>
              <Button
                onClick={() =>
                  onRestore({
                    allowed_domains:
                      row.configuration?.allowed_domains ?? [],
                    allowed_ips: row.configuration?.allowed_ips ?? []
                  })
                }
                size="sm"
                type="button"
                variant="outline"
              >
                Restore this state
              </Button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <Collapsible
      className="px-4 py-2 space-y-2 border border-border rounded-md"
      onOpenChange={setOpen}
      open={open}
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between cursor-pointer">
          <h4 className="text-sm font-semibold text-primary">Recent changes</h4>
          <Button
            className="p-0 w-9"
            size="sm"
            type="button"
            variant="ghost"
          >
            {open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="sr-only">Toggle recent changes</span>
          </Button>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2">{renderBody()}</CollapsibleContent>
    </Collapsible>
  )
}
